import { hasDesignStudioUsage, isObjectOrRelationshipAttribute } from './item';
import type { DataIndexItemKind, DataIndexUsageKey } from './types';

/**
 * Which usage surfaces an item has at all.
 *
 * Not how many things are on them: a surface is absent because the plan has
 * no such thing or the workspace does not show it, which is a different
 * answer from a surface with nothing on it. The drawer's accordions and the
 * item page's views each list the surfaces in their own order, and both ask
 * here which ones exist, so the two cannot drift apart.
 */

export type UsageScope = {
  kind: DataIndexItemKind;
  /** Builder plans have no segments, campaigns or layouts. */
  isBuilder: boolean;
  /** Whether the workspace shows content usage for object and relationship attributes. */
  withTemplatesSnippetsLayouts: boolean;
};

/** Whether an item shows content usage at all. */
export function withContentUsage(scope: UsageScope): boolean {
  return isObjectOrRelationshipAttribute(scope.kind) ? scope.withTemplatesSnippetsLayouts : true;
}

function isOffered(id: DataIndexUsageKey, scope: UsageScope): boolean {
  const withContent = withContentUsage(scope);

  switch (id) {
    case 'segments':
    case 'campaigns':
      return !scope.isBuilder;
    case 'newsletters':
      return true;
    case 'templates':
    case 'snippets':
      return withContent;
    case 'design_studio':
      return hasDesignStudioUsage(scope.kind, withContent);
    case 'layouts':
      return withContent && !scope.isBuilder;
    default: {
      const _exhaustive: never = id;
      throw new Error(`unhandled usage surface: ${String(_exhaustive)}`);
    }
  }
}

/** The surfaces this item has, out of the ones a caller lists, in that order. */
export function offeredUsageSurfaces(order: readonly DataIndexUsageKey[], scope: UsageScope): DataIndexUsageKey[] {
  return order.filter((id) => isOffered(id, scope));
}
